import Heading from 'components/Basic/Heading/Heading';
import { ProductItemSkeleton } from 'components/Blocks/Product/List/ListedItem/ProductItemSkeleton';
import ProductsList, { ProductListTypes } from 'components/Blocks/Product/List/ProductsList';
import Button from 'components/Forms/Button/Button';
import { showInfoMessage } from 'components/Helpers/Toasts';
import Webline from 'components/Layout/Webline/Webline';
import { mapListedProductType } from 'connectors/products/Products';
import { createEmptyArray } from 'helpers/arrayUtils';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useDomainConfig } from 'hooks/useDomainConfig';
import { useWishlist } from 'hooks/wishlist/useWishlist';
import { useRouter } from 'next/router';
import { CopyIcon, RemoveThinIcon } from 'public/svg/IconsSvg';

export const Wishlist: FC = () => {
    const t = useTypedTranslationFunction();
    const { wishlist, fetching, handleCleanWishlist } = useWishlist();
    const { url, currencyCode } = useDomainConfig();
    const router = useRouter();
    const windowLocation = typeof window !== 'undefined' ? window.location.href : '';

    const buildShareUrl = (): string => {
        if (!wishlist) {
            return windowLocation;
        }

        return (
            url.replace(/\/$/, '') +
            '/' +
            router.asPath.replace(/^\//, '') +
            '?id=' +
            wishlist.products.map((product) => product.catalogNumber).join()
        );
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(buildShareUrl());

        showInfoMessage(t('Link was successfully copied to clipboard'));
    };

    if (fetching) {
        return (
            <Webline>
                {createEmptyArray(6).map((_, index) => (
                    <ProductItemSkeleton key={index} />
                ))}
            </Webline>
        );
    }

    const wishlistProducts = [];
    if (wishlist?.products) {
        for (const productData of wishlist.products) {
            wishlistProducts.push(mapListedProductType(productData, currencyCode));
        }
    }

    return (
        <>
            <Webline>
                <Heading type="h1" className="!text-big lg:!text-h1">
                    {t('My favorites')}
                </Heading>
                {!!wishlist?.products.length && (
                    <div className="border-greyLighter flex w-full flex-col justify-between border-b pb-2 md:flex-row md:flex-row md:items-center">
                        <Button
                            className="whitespace-nowrap bg-grayLight text-grayVeryDark hover:bg-black hover:text-grayVeryLight"
                            size="smallNarrow"
                            onClick={() => {
                                handleCleanWishlist();
                            }}
                        >
                            <span className="text-sm mr-3">{t('Delete all from wishlist')}</span>
                            <RemoveThinIcon className="w-3 text-grayVeryDark hover:text-grayVeryLight" />
                        </Button>

                        <Button size="small" type="submit" onClick={handleCopyLink} className="mt-2 lg:mt-0">
                            <CopyIcon className="w-6" />
                            {t('Share wishlist via link')}
                        </Button>
                    </div>
                )}

                {wishlist?.products.length ? (
                    <div>
                        <ProductsList
                            isLoading={fetching}
                            gtmListName="wishlist"
                            products={wishlistProducts}
                            type={ProductListTypes.Normal}
                        />
                    </div>
                ) : (
                    <div>
                        <strong>{t('There are no products in the wishlist. Add some first.')}</strong>
                    </div>
                )}
            </Webline>
        </>
    );
};

export default Wishlist;
